import React from 'react';
import ReactMarkdown from 'react-markdown';
import HttpMethodBadge from './HttpMethodBadge';
import SchemaTree from './SchemaTree';
import { getPrimaryContent, getSchemaType, resolveSchema } from '../utils/openapi';
import { createGroupAnchor } from '../utils/seo';

function ParameterTable({ title, parameters }) {
  if (!parameters.length) {
    return null;
  }

  return (
    <section className="space-y-2">
      <h3 className="text-sm font-semibold uppercase tracking-[0.14em] text-slate-500">{title}</h3>
      <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800">
        <table className="w-full min-w-[520px] border-collapse text-left text-sm">
          <thead className="bg-slate-50 dark:bg-slate-900/70">
            <tr>
              <th className="px-3 py-2 font-semibold text-slate-700 dark:text-slate-300">Name</th>
              <th className="px-3 py-2 font-semibold text-slate-700 dark:text-slate-300">Required</th>
              <th className="px-3 py-2 font-semibold text-slate-700 dark:text-slate-300">Type</th>
              <th className="px-3 py-2 font-semibold text-slate-700 dark:text-slate-300">Description</th>
            </tr>
          </thead>
          <tbody>
            {parameters.map((parameter) => (
              <tr key={`${parameter.in}:${parameter.name}`} className="border-t border-slate-200 dark:border-slate-800">
                <td className="px-3 py-2 font-mono text-xs text-slate-800 dark:text-slate-200">{parameter.name}</td>
                <td className="px-3 py-2 text-xs text-slate-700 dark:text-slate-300">{parameter.required ? 'Yes' : 'No'}</td>
                <td className="px-3 py-2 text-xs text-slate-700 dark:text-slate-300">{getSchemaType(parameter.schema)}</td>
                <td className="px-3 py-2 text-xs text-slate-700 dark:text-slate-300">{parameter.description || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function EndpointDoc({ spec, operation, selectedOverviewId }) {
  if (!operation) {
    if (selectedOverviewId === 'authentication') {
      return (
        <section className="mx-auto w-full max-w-[800px] space-y-4 rounded-2xl border border-slate-200 bg-white p-6 text-slate-700 shadow-panel dark:border-slate-800 dark:bg-slate-950 dark:text-slate-200">
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-50">Authentication</h1>
          <p>
            Authenticate by logging into your Hawktrion account and generating a token from the integration portal.
          </p>
          <div className="space-y-2 rounded-xl border border-slate-200 p-4 dark:border-slate-800">
            <p>
              <span className="font-semibold">Get token URL:</span>{' '}
              <a
                href="https://waba.hawktrion.com/integrations/token"
                target="_blank"
                rel="noreferrer"
                className="text-cyan-600 hover:underline dark:text-cyan-400"
              >
                https://waba.hawktrion.com/integrations/token
              </a>
            </p>
            <p>
              <span className="font-semibold">Login URL:</span>{' '}
              <a
                href="https://waba.hawktrion.com/signin"
                target="_blank"
                rel="noreferrer"
                className="text-cyan-600 hover:underline dark:text-cyan-400"
              >
                https://waba.hawktrion.com/signin
              </a>
            </p>
          </div>
          <p className="text-sm text-slate-600 dark:text-slate-300">
            After generating your token, paste it in the Authentication field in the request playground to authorize API calls.
          </p>
        </section>
      );
    }

    if (selectedOverviewId === 'servers') {
      return (
        <section className="mx-auto w-full max-w-[800px] rounded-2xl border border-slate-200 bg-white p-6 text-slate-700 shadow-panel dark:border-slate-800 dark:bg-slate-950 dark:text-slate-200">
          <h1 className="mb-2 text-2xl font-bold text-slate-900 dark:text-slate-50">Servers</h1>
          <p>Select an environment in the request playground to target a specific API server.</p>
        </section>
      );
    }

    if (selectedOverviewId === 'terms') {
      return (
        <section className="mx-auto w-full max-w-[800px] rounded-2xl border border-slate-200 bg-white p-6 text-slate-700 shadow-panel dark:border-slate-800 dark:bg-slate-950 dark:text-slate-200">
          <h1 className="mb-2 text-2xl font-bold text-slate-900 dark:text-slate-50">Terms</h1>
          <p>Use this API according to your Hawktrion and WhatsApp Business platform agreements.</p>
        </section>
      );
    }

    return (
      <section className="mx-auto w-full max-w-[800px] rounded-2xl border border-slate-200 bg-white p-6 text-slate-700 shadow-panel dark:border-slate-800 dark:bg-slate-950 dark:text-slate-200">
        Select an endpoint from the left sidebar.
      </section>
    );
  }

  const parameters = operation.parameters || [];
  const pathParameters = parameters.filter((param) => param.in === 'path');
  const queryParameters = parameters.filter((param) => param.in === 'query');
  const headerParameters = parameters.filter((param) => param.in === 'header');

  const requestContent = getPrimaryContent(operation.requestBody?.content || {});
  const requestSchema = resolveSchema(spec, requestContent.schema);

  return (
    <article className="mx-auto w-full max-w-[800px] space-y-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-panel dark:border-slate-800 dark:bg-slate-950">
      <header className="space-y-3 border-b border-slate-200 pb-4 dark:border-slate-800">
        <nav aria-label="Breadcrumb" className="text-xs text-slate-500 dark:text-slate-400">
          <ol className="flex flex-wrap items-center gap-2">
            <li><a href="/" className="hover:underline">API documentation</a></li>
            <li aria-hidden="true">/</li>
            <li>
              <a href={`/#${createGroupAnchor(operation.tags?.[0] || 'General')}`} className="hover:underline">
                {operation.tags?.[0] || 'General'}
              </a>
            </li>
            <li aria-hidden="true">/</li>
            <li aria-current="page">{operation.summary}</li>
          </ol>
        </nav>
        <div className="flex items-center gap-3">
          <HttpMethodBadge method={operation.method} />
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-50">{operation.summary}</h1>
        </div>
        <p className="font-mono text-sm text-slate-600 dark:text-slate-300">{operation.path}</p>
      </header>

      <section className="prose prose-slate max-w-none text-sm dark:prose-invert">
        <ReactMarkdown>
          {operation.description || `Use this ${operation.tags?.[0] || 'WhatsApp Business'} endpoint to ${operation.summary.toLowerCase()}.`}
        </ReactMarkdown>
      </section>

      <section className="space-y-5">
        <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">Parameters</h2>
        <ParameterTable title="Path Parameters" parameters={pathParameters} />
        <ParameterTable title="Query Parameters" parameters={queryParameters} />
        <ParameterTable title="Header Parameters" parameters={headerParameters} />
      </section>

      <section className="space-y-3">
        <details className="group rounded-xl border border-slate-200 p-3 dark:border-slate-800" open>
          <summary className="cursor-pointer list-none text-base font-semibold text-slate-900 dark:text-slate-100">
            Request Body Schema
          </summary>
          <div className="mt-3">
            <SchemaTree title={requestContent.mediaType || 'No media type'} schema={requestSchema} spec={spec} />
          </div>
        </details>
      </section>

      <section className="space-y-3">
        <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">Responses</h2>
        {Object.entries(operation.responses || {}).map(([status, response]) => {
          const responseContent = getPrimaryContent(response.content || {});
          const responseSchema = resolveSchema(spec, responseContent.schema);

          return (
            <details key={status} className="rounded-xl border border-slate-200 p-3 dark:border-slate-800" open={status === '200'}>
              <summary className="cursor-pointer list-none">
                <div className="flex items-center gap-3">
                  <span className="rounded-md bg-slate-900 px-2 py-1 font-mono text-xs font-semibold text-white dark:bg-slate-100 dark:text-slate-900">
                    {status}
                  </span>
                  <p className="text-sm text-slate-700 dark:text-slate-300">{response.description || 'Response'}</p>
                </div>
              </summary>
              <div className="mt-3">
                <SchemaTree title={responseContent.mediaType || 'No body'} schema={responseSchema} spec={spec} />
              </div>
            </details>
          );
        })}
      </section>
      <nav aria-label="Related API endpoints" className="border-t border-slate-200 pt-4 dark:border-slate-800">
        <h2 className="mb-2 text-sm font-semibold text-slate-900 dark:text-slate-100">Related API endpoints</h2>
        <a href="/" className="text-sm text-cyan-700 hover:underline dark:text-cyan-400">Browse all WhatsApp Business API endpoints</a>
      </nav>
    </article>
  );
}

export default EndpointDoc;
